import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef, useState } from 'react';
import { TourDetail as Detail, TourStopDetail } from '../types';
import PackingList from './PackingList';
import { ctrlWheelZoom } from './mapZoom';

/** Boja čiode po stanju stanice — ista podela kao u spisku ispod mape. */
const PIN_KIND: Record<string, string> = {
  'gotova': 'done',
  'na njoj': 'now',
  'izuzetak': 'bad',
  'čeka': 'wait'
};

function pin(state: string, text: string): L.DivIcon {
  return L.divIcon({
    className: `rpin rpin--${PIN_KIND[state] ?? 'wait'}`,
    html: `<span>${text}</span>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13]
  });
}

/**
 * Mapa ture u toku.
 *
 * Odvojena od mape predloga: tamo se tura sastavlja i klik nešto menja, ovde se
 * gleda šta se već desilo i ništa se ne dira. Spajanje to dvoje značilo bi mapu
 * koja u pola slučajeva ne sme da reaguje na klik.
 */
function TourMap({ stops }: { stops: TourStopDetail[] }) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);

  const located = stops.filter(
    (s): s is TourStopDetail & { latitude: number; longitude: number } =>
      s.latitude !== null && s.longitude !== null
  );

  useEffect(() => {
    if (!container.current || map.current) return;

    const instance = L.map(container.current, { scrollWheelZoom: false }).setView([45.77, 19.11], 9);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(instance);

    map.current = instance;
    layer.current = L.layerGroup().addTo(instance);

    const odjavi = ctrlWheelZoom(instance, container.current);

    return () => {
      odjavi();
      instance.remove();
      map.current = null;
      layer.current = null;
    };
  }, []);

  useEffect(() => {
    const instance = map.current;
    const group = layer.current;
    if (!instance || !group) return;

    group.clearLayers();

    const points = located.map((s) => [s.latitude, s.longitude] as [number, number]);

    if (points.length > 1) {
      // Linija ide redom kojim je vozilo STVARNO išlo, jer je vozač smeo da
      // promeni redosled — nacrtan plan bi ovde bio slika ture koja se nije
      // dogodila.
      L.polyline(points, { color: '#17527d', weight: 2, opacity: 0.5, dashArray: '5 6' }).addTo(group);
    }

    located.forEach((stop) => {
      L.marker([stop.latitude, stop.longitude], { icon: pin(stop.state, String(stop.sequence)) })
        .bindTooltip(
          `${stop.sequence}. ${stop.name}${stop.city ? `, ${stop.city}` : ''}`
          + `<br>${stop.state}`
          + (stop.actualArrival ? `<br>stigao ${time(stop.actualArrival)}` : '')
          + (stop.actualDeparture ? ` · otišao ${time(stop.actualDeparture)}` : '')
          + (stop.exceptionReason ? `<br><b>${stop.exceptionReason}</b>` : '')
        )
        .addTo(group);
    });

    instance.invalidateSize();

    if (points.length > 1) {
      instance.fitBounds(L.latLngBounds(points).pad(0.12));
    } else if (points.length === 1) {
      instance.setView(points[0], 13);
    }
  }, [stops]);

  return <div className="tourmap" ref={container} />;
}

interface Props {
  tour: Detail;
  onClose: () => void;
}

/**
 * Jedna tura, otvorena.
 *
 * Spisak tura kaže „5/8 stanica“, a to je premalo čim nešto krene naopako:
 * operater tada mora da zna GDE je vozilo stalo, šta je predato i šta ostaje u
 * vozilu. Zato ovde stoji sve zapisano sa terena — pa i planirano vreme uz
 * stvarno, jer je razlika između to dvoje ono što se meri od prvog dana.
 */
export default function TourDetail({ tour, onClose }: Props) {
  const [packing, setPacking] = useState(false);
  const withoutPoint = tour.stops.filter((s) => s.latitude === null || s.longitude === null);

  if (packing) {
    return <PackingList tourId={tour.tourId} onClose={() => setPacking(false)} />;
  }

  return (
    <div className="tourdetail">
      <div className="tourdetail__head">
        <h3>
          {tour.name}
          <span className="muted">
            {' '}· {tour.driver ?? 'bez vozača'} · {tour.vehicle ?? 'bez vozila'}
          </span>
        </h3>
        <span className="tourdetail__actions">
          <button type="button" onClick={() => setPacking(true)}>Nalog za pakovanje</button>
          <button type="button" className="link" onClick={onClose}>Zatvori</button>
        </span>
      </div>

      <p className="muted">
        {tour.actualStart ? `Krenula ${time(tour.actualStart)}` : 'Još nije krenula'}
        {tour.actualEnd && ` · završena ${time(tour.actualEnd)}`}
        {tour.lastSyncAt && ` · poslednje javljanje ${time(tour.lastSyncAt)}`}
      </p>

      <TourMap stops={tour.stops} />

      {withoutPoint.length > 0 && (
        <p className="note">
          Bez tačke na mapi: {withoutPoint.map((s) => s.name).join(', ')}. Stanica se
          vidi u spisku, ali se ne crta — adresa nema potvrđenu koordinatu.
        </p>
      )}

      <ul className="tourstops">
        {tour.stops.map((stop) => (
          <li key={stop.stopId} className={`tourstops__row tourstops__row--${PIN_KIND[stop.state] ?? 'wait'}`}>
            <span className="tourstops__no">{stop.sequence}</span>

            <div className="tourstops__body">
              <p className="tourstops__where">
                {stop.name}
                {stop.city ? `, ${stop.city}` : ''}
                {stop.isPickup && <span className="flag"> roba se uzima</span>}
                {stop.requiredTwoPeople && <span className="flag"> dva čoveka</span>}
                {stop.hasSignature && <span className="flag flag--ok"> potpisano</span>}
              </p>

              <p className="muted tourstops__times">
                {stop.actualArrival
                  ? `stigao ${time(stop.actualArrival)}`
                  : stop.plannedArrival
                    ? `planirano ${time(stop.plannedArrival)}`
                    : 'bez vremena'}
                {stop.actualDeparture && ` · otišao ${time(stop.actualDeparture)}`}
                {/* Razlika plan–stvarno je ono zbog čega se vremena i beleže.
                    Bez nje bi ekran imao dva broja koja niko ne poredi. */}
                {stop.plannedArrival && stop.actualArrival && (
                  <span className="tourstops__delta"> · {lateness(stop.plannedArrival, stop.actualArrival)}</span>
                )}
              </p>

              {stop.items.length > 0 && (
                <p className="tourstops__items">
                  {stop.items.map((item) => (
                    <span
                      key={`${stop.stopId}-${item.articleCode}-${item.articleName}`}
                      className={item.isDelivered ? 'done' : 'pending'}
                    >
                      {item.isDelivered ? '✓ ' : '· '}
                      {item.articleName} × {item.quantity}
                    </span>
                  ))}
                </p>
              )}

              {stop.exceptionReason && (
                <p className="tourstops__exception">
                  {stop.exceptionReason}
                  {stop.exceptionResolution
                    ? ` — odlučeno: ${stop.exceptionResolution}`
                    : ' — čeka odluku'}
                </p>
              )}

              {/* Zašto je vozač promenio redosled. To je znanje o terenu i zbog
                  toga se i traži jednim tapom — ne sme da ostane samo u bazi. */}
              {stop.reorderReason && (
                <p className="muted">Redosled promenjen: {stop.reorderReason}</p>
              )}

              {stop.notes && <p className="muted">Beleška: {stop.notes}</p>}

              {stop.phones.length > 0 && (
                <p className="tourstops__phones">
                  {stop.phones.map((number) => (
                    <a key={number} href={`tel:${number}`}>{number}</a>
                  ))}
                </p>
              )}
            </div>

            <span className={`flag flag--${flagOf(stop.state)}`}>{stop.state}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function flagOf(state: string): string {
  if (state === 'gotova') return 'ok';
  if (state === 'izuzetak') return 'bad';
  return 'warn';
}

function time(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
}

/** „18 min kasnije“ ili „6 min ranije“ — minuti u odnosu na plan. */
function lateness(planned: string, actual: string): string {
  const minutes = Math.round(
    (new Date(actual).getTime() - new Date(planned).getTime()) / 60000
  );

  if (!Number.isFinite(minutes) || minutes === 0) return 'u minut';
  return minutes > 0 ? `${minutes} min kasnije` : `${-minutes} min ranije`;
}
